import React, { useState, useEffect, useRef } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import avatar from "../Images/profile.jpg";
import { useAuth } from "../context/AuthContext";

const links = [
  { name: "Home", to: "/" },
  { name: "Services", to: "/services" },

  { name: "Categories", to: "/categories" },
  { name: "Locations", to: "/locations" },

  { name: "contact", to: "/contact" },
  { name: "Blogs", to: "/blogs" },
  { name: "About", to: "/about" },
];

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { isAuthenticated, role, logout } = useAuth();

  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const toggleMobile = () => setMobileOpen(!mobileOpen);
  const handleLogout = () => {
    logout();
    navigate("/");
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    const onLogout = () => handleLogout();
    window.addEventListener("oim-logout", onLogout);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  });

  return (
    <nav className="bg-transparent mulish-light sticky top-0 z-50 grid grid-cols-2 px-6 md:px-10 lg:px-20 py-3 items-center">
      {/* Left Section */}
      <div className="max-w-7xl w-full mx-auto px-4 py-2 poppins-regular gap-10 flex justify-start items-center">
        <RouterLink to="/">
          <h1 className="text-4xl md:text-6xl anton-regular font-extrabold tracking-wider bg-gradient-to-r from-orange-500 via-white to-green-500 bg-clip-text text-transparent drop-shadow-[0_0_3px_rgba(0,0,0,0.2)] cursor-pointer">
            OIM
          </h1>
        </RouterLink>

        {/* Desktop Links */}
        <ul className="hidden lg:flex text-lg space-x-6 uppercase tracking-wide mulish-bold ">
          {links.map((link, index) => (
            <li key={index} className="relative group">
              {link.dropdown ? (
                <>
                  <span className="cursor-pointer  bg-gradient-to-r from-white to-neutral-400 bg-clip-text text-transparent hover:text-orange-500">
                    {link.name}
                  </span>
                  <ul className="absolute top-full left-0 border poppins-regular text-white shadow-xl rounded-xl grid grid-cols-1 gap-2 p-3 z-50 w-max opacity-0 group-hover:opacity-100 invisible group-hover:visible transition duration-300">
                    {link.dropdown.map((item, idx) => (
                      <li key={idx}>
                        <RouterLink
                          to={item.to}
                          className="block w-full px-2 py-1 text-base poppins-regular bg-gradient-to-r from-white to-neutral-400 bg-clip-text text-transparent hover:text-black rounded-md hover:bg-clip-padding"
                        >
                          {item.name}
                        </RouterLink>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <RouterLink
                  to={link.to}
                  className="bg-gradient-to-r from-white to-neutral-400 bg-clip-text text-transparent drop-shadow-[0_0_2px_rgba(0,0,0,0.3)] hover:text-orange-500"
                >
                  {link.name}
                </RouterLink>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* Right Section */}
      <div className="flex items-center w-full text-sm md:text-lg justify-end gap-2 md:gap-4 ">
        {!isAuthenticated ? (
          <>
            <button
              onClick={() => navigate("/signup")}
              className="bg-gradient-to-r text-xs md:text-lg from-white to-neutral-400 uppercase px-2 py-1 md:px-4 md:py-2 rounded-lg mulish-bold hover:text-orange-500 transition"
            >
              SignUp
            </button>
            <button
              onClick={() => navigate("/signin")}
              className="bg-gradient-to-r text-xs md:text-lg from-white to-neutral-400 uppercase px-2 py-1 md:px-4 md:py-2 rounded-lg mulish-bold hover:text-orange-500 transition"
            >
              SignIn
            </button>
          </>
        ) : (
          <>
            {role === "provider" && (
              <button
                onClick={() => navigate("/provider/create")}
                className="bg-gradient-to-r from-white to-neutral-400 uppercase px-4 py-1 rounded-lg mulish-bold hover:text-orange-500 transition hidden md:block"
              >
                + Add Listing
              </button>
            )}
            {role === "channel_partner" && (
              <button
                onClick={() => navigate("/channelpartner/create-promoter")}
                className="bg-gradient-to-r from-white to-neutral-400 uppercase px-4 py-1 rounded-lg mulish-bold hover:text-orange-500 transition hidden md:block"
              >
                + Add Promoter
              </button>
            )}
            {role === "admin" && (
              <button
                onClick={() => navigate("/dashboard/create-channel-partner")}
                className="bg-gradient-to-r from-white to-neutral-400 uppercase px-4 py-1 rounded-lg mulish-bold hover:text-orange-500 transition hidden md:block"
              >
                + Add Partner
              </button>
            )}
            <div className="relative" ref={dropdownRef}>
              <img
                src={avatar}
                alt="profile"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="w-10 h-10 rounded-full cursor-pointer border-2 border-white"
              />
              {dropdownOpen && (
                <ul className="absolute right-0 mt-2 w-48 text-white rounded-lg shadow-lg py-2 z-50 bg-gradient-to-r from-white to-neutral-400 bg-clip-text text-transparent drop-shadow-[0_0_2px_rgba(0,0,0,0.3)] ">
                  <li>
                    <RouterLink
                      to={
                        role === "admin"
                          ? "/dashboard"
                          : role === "provider"
                          ? "/provider"
                          : role === "channel_partner"
                          ? "/channelpartner"
                          : role === "promoter"
                          ? "/promoter"
                          : "/"
                      }
                      className="block px-4 py-2 poppins-regular rounded-lg hover:bg-clip-padding bg-gradient-to-r from-white to-neutral-400 bg-clip-text text-transparent hover:text-orange-500"
                    >
                      Dashboard
                    </RouterLink>
                  </li>
                  {/* <li>
                    <RouterLink
                      to="/change-password"
                      className="block px-4 py-2 poppins-regular rounded-lg hover:bg-clip-padding  bg-gradient-to-r from-white to-neutral-400 bg-clip-text text-transparent  hover:text-orange-500"
                    >
                      Change Password
                    </RouterLink>
                  </li> */}
                  <li>
                    <button
                      onClick={handleLogout}
                      className="block w-full poppins-regular text-left px-4 py-2 rounded-lg hover:bg-clip-padding  bg-gradient-to-r from-white to-neutral-400 bg-clip-text text-transparent  hover:text-orange-500"
                    >
                      Logout
                    </button>
                  </li>
                </ul>
              )}
            </div>
          </>
        )}

        {/* Mobile Toggle */}
        <div className="lg:hidden">
          <button onClick={toggleMobile}>
            {mobileOpen ? (
              <X className="w-6 h-6 text-white" />
            ) : (
              <Menu className="w-6 h-6 text-white" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden col-span-2 poppins-regular bg-white text-black px-6 pt-4 pb-6 space-y-4 rounded-lg">
          <ul className="space-y-4 text-sm md:text-base">
            {links.map((link, index) =>
              link.dropdown ? (
                <li key={index}>
                  <span className="font-semibold text-orange-500">
                    {link.name}
                  </span>
                  <ul className="ml-4 mt-1 grid grid-cols-1 gap-2">
                    {link.dropdown.map((item, idx) => (
                      <li key={idx}>
                        <RouterLink
                          to={item.to}
                          onClick={() => setMobileOpen(false)}
                          className="text-sm text-black hover:text-orange-500"
                        >
                          {item.name}
                        </RouterLink>
                      </li>
                    ))}
                  </ul>
                </li>
              ) : (
                <li key={index}>
                  <RouterLink
                    to={link.to}
                    onClick={() => setMobileOpen(false)}
                    className="text-black hover:text-orange-500"
                  >
                    {link.name}
                  </RouterLink>
                </li>
              )
            )}
            {!isAuthenticated ? (
              <>
                <li>
                  <RouterLink
                    to="/signup"
                    className="text-black  hover:text-orange-500"
                    onClick={() => setMobileOpen(false)}
                  >
                    Sign-Up
                  </RouterLink>
                </li>
                <li>
                  <RouterLink
                    to="/signin"
                    className="text-black hover:text-orange-500"
                    onClick={() => setMobileOpen(false)}
                  >
                    Sign-In
                  </RouterLink>
                </li>
              </>
            ) : (
              <>
                {role === "provider" && (
                  <li>
                    <RouterLink
                      to="/provider/create"
                      className="text-black hover:text-orange-500"
                      onClick={() => setMobileOpen(false)}
                    >
                      + Add Listing
                    </RouterLink>
                  </li>
                )}
                {role === "channel_partner" && (
                  <li>
                    <RouterLink
                      to="/channelpartner/create-promoter"
                      className="text-black hover:text-orange-500"
                      onClick={() => setMobileOpen(false)}
                    >
                      + Add Promoter
                    </RouterLink>
                  </li>
                )}
                {role === "admin" && (
                  <li>
                    <RouterLink
                      to="/dashboard/create-channel-partner"
                      className="text-black hover:text-orange-500"
                      onClick={() => setMobileOpen(false)}
                    >
                      + Add Channel Partner
                    </RouterLink>
                  </li>
                )}
                <li>
                  <RouterLink
                    to={
                      role === "admin"
                        ? "/dashboard"
                        : role === "provider"
                        ? "/provider"
                        : role === "channel_partner"
                        ? "/channelpartner"
                        : role === "promoter"
                        ? "/promoter"
                        : "/"
                    }
                    className="text-black hover:text-orange-500"
                    onClick={() => setMobileOpen(false)}
                  >
                    Dashboard
                  </RouterLink>
                </li>
                {/* <li>
                  <RouterLink
                    to="/change-password"
                    className="text-black hover:text-orange-500"
                    onClick={() => setMobileOpen(false)}
                  >
                    Change Password
                  </RouterLink>
                </li> */}
                <li>
                  <button
                    onClick={() => {
                      logout();
                      setMobileOpen(false);
                      navigate("/");
                    }}
                    className="text-black hover:text-orange-500"
                  >
                    Logout
                  </button>
                </li>
              </>
            )}
          </ul>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
